<template>
  <PageHeader>
    <template #header-title>{{ $t('Employments') }}</template>
    <template #header-description>{{ $t('Review and onboard workers linked to jobs.') }}</template>
  </PageHeader>

  <div class="tw-mb-4">
    <WdsTabs v-model:active-tab-key="activeTabKey">
      <WdsTab :tab-key="'new'" title="New" data-test="employments-new-tab">
        <template #title>
          <WdsStack align-center gap-sm>
            {{ $t('New') }}
            <WdsLabel v-if="newEmploymentsCount > 0" xs>{{ newEmploymentsCount }}</WdsLabel>
          </WdsStack>
        </template>
        <div class="tw-mt-2">
          <EmploymentsNewTable
            :is-active-tab="activeTabKey === 'new'"
            @employment-onboarded="handleEmploymentOnboarded"
          />
        </div>
      </WdsTab>
      <WdsTab :tab-key="'updated'" title="Updated" data-test="employments-updated-tab">
        <template #title>
          <WdsStack align-center gap-sm>
            {{ $t('Updated') }}
            <WdsLabel v-if="updatedEmploymentsCount > 0" xs>{{ updatedEmploymentsCount }}</WdsLabel>
          </WdsStack>
        </template>
        <div class="tw-mt-2">
          <EmploymentsUpdatedTable
            :is-active-tab="activeTabKey === 'updated'"
            @employment-updates-approved="handleEmploymentUpdatesApproved"
          />
        </div>
      </WdsTab>
      <WdsTab :tab-key="'onboarded'" title="Onboarded" data-test="employments-onboarded-tab">
        <template #title>
          <WdsStack align-center gap-sm>
            {{ $t('Onboarded') }}
            <WdsLabel v-if="onboardedEmploymentsCount > 0" xs>
              {{ onboardedEmploymentsCount }}
            </WdsLabel>
          </WdsStack>
        </template>
        <div class="tw-mt-2">
          <EmploymentsOnboardedTable :is-active-tab="activeTabKey === 'onboarded'" />
        </div>
      </WdsTab>
    </WdsTabs>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useQuery, useApolloClient } from '@vue/apollo-composable'
import { WdsTabs, WdsTab, WdsStack, WdsLabel } from '@worksome/wds-components'
import { graphql } from '@/gql'
import { $t } from '@/composables/translation'
import { Message } from '@/utils/notification'
import PageHeader from '@/components/PageHeader.vue'
import EmploymentsNewTable from '@/components/Employments/Partner/EmploymentsNewTable.vue'
import EmploymentsUpdatedTable from '@/components/Employments/Partner/EmploymentsUpdatedTable.vue'
import EmploymentsOnboardedTable from '@/components/Employments/Partner/EmploymentsOnboardedTable.vue'

const activeTabKey = ref('new')
const EMPLOYMENT_COUNTS = graphql(`
  query EmploymentCounts($accounts: [ID!]!) {
    employmentPendingCount: employments(accounts: $accounts, employerRecordStatus: [INCOMPLETE]) {
      paginatorInfo {
        total
      }
    }
    employmentChangesRequestedCount: employments(
      accounts: $accounts
      employerRecordStatus: [CHANGES_REQUESTED]
    ) {
      paginatorInfo {
        total
      }
    }
    employmentCompleteCount: employments(accounts: $accounts, employerRecordStatus: [COMPLETE]) {
      paginatorInfo {
        total
      }
    }
  }
`)

const { result: countsResult, refetch: refetchCounts } = useQuery(
  EMPLOYMENT_COUNTS,
  () => ({
    accounts: [currentAccount.globalId],
  }),
  {
    clientId: 'publicApi',
  },
)

const newEmploymentsCount = computed(
  () => countsResult.value?.employmentPendingCount.paginatorInfo.total ?? 0,
)
const updatedEmploymentsCount = computed(
  () => countsResult.value?.employmentChangesRequestedCount.paginatorInfo.total ?? 0,
)
const onboardedEmploymentsCount = computed(
  () => countsResult.value?.employmentCompleteCount.paginatorInfo.total ?? 0,
)

const { client } = useApolloClient('publicApi')

const handleEmploymentUpdatesApproved = async () => {
  await client.refetchQueries({
    include: [
      'PartnerEmploymentsUpdatedQuery',
      'PartnerEmploymentsOnboardedQuery',
      'EmploymentCounts',
    ],
  })

  refetchCounts()

  // Show success notification using the existing notification system
  const notification = new Message('Employment updated successfully')
  notification.dispatch()
}
const handleEmploymentOnboarded = async () => {
  // Use Apollo's refetchQueries to refresh all employment-related data
  await client.refetchQueries({
    include: [
      'PartnerEmploymentsNewQuery',
      'PartnerEmploymentsUpdatedQuery',
      'PartnerEmploymentsOnboardedQuery',
      'EmploymentCounts',
    ],
  })

  refetchCounts()

  // Show success notification using the existing notification system
  const notification = new Message('Employment onboarded successfully')
  notification.dispatch()
}

onMounted(() => {
  refetchCounts()
})
</script>
